<template>
    <div class="h-100">
        <b-container class="m-2">
            <b-row align-h="between">
                <b-col class="d-flex" cols="12" md="8">
                    <search-bar @searchValueChanged="(value) => (searchQuery = value)" />

                    <b-dropdown
                        v-model="perPage"
                        v-b-tooltip.hover.bottom="$t('paginator.number-of-items-per-page')"
                        :text="`${$t('paginator.entries')}: ${perPage.toString()}`"
                        class="per-page-selector mx-50 round-medium"
                        no-caret
                        variant="light"
                    >
                        <b-dropdown-item
                            v-for="perPageOption in perPageOptions"
                            :key="perPageOption"
                            @click="perPage = perPageOption"
                        >
                            {{ perPageOption }}
                        </b-dropdown-item>
                    </b-dropdown>
                </b-col>

                <b-col cols="auto">
                    <b-button id="contacts-add" variant="primary" @click="addList">
                        <font-awesome-icon class="mr-50" icon="plus" />
                        <span class="text-nowrap">{{ $t("contacts.lists.button-add") }}</span>
                    </b-button>
                </b-col>
            </b-row>
        </b-container>

        <b-table
            ref="refTable"
            :empty-text="$t('paginator.no-matching-records-found')"
            :fields="columns"
            :items="fetchContactsLists"
            :no-sort-reset="true"
            :table-busy="isLoading"
            class="position-relative"
            no-select-on-click
            primary-key="id"
            responsive
            select-mode="single"
            selectable
            show-empty
            @row-clicked="rowClicked"
        >
            <template #table-busy>
                <div class="d-flex justify-content-center my-1">
                    <b-spinner />
                </div>
            </template>

            <template #cell(name)="data">
                <strong>{{ data.item.name }}</strong>
            </template>
            <template #cell(checkbox)="data">
                <b-form-radio
                    v-model.number="selected"
                    :value="data.item.id"
                    class="p-0 m-0"
                    name="lists"
                    @change="rowClicked(data.item, data.index)"
                />
            </template>
            <template #cell(contacts)="data">
                {{ data.item.contacts.length }}
            </template>
        </b-table>

        <div class="mx-2 mb-2 mt-4">
            <b-row>
                <b-col
                    class="d-flex align-items-center justify-content-center justify-content-sm-start"
                    cols="12"
                    sm="6"
                >
                    <span class="text-muted">
                        {{ $t("paginator.showing") }} {{ dataMeta.to }} {{ $t("paginator.of") }} {{ dataMeta.of }}
                        {{ $t("paginator.entries") }}
                    </span>
                </b-col>

                <b-col class="d-flex align-items-center justify-content-center justify-content-sm-end" cols="12" sm="6">
                    <b-pagination
                        v-model="currentPage"
                        :per-page="perPage"
                        :total-rows="total"
                        class="mb-0 mt-1 mt-sm-0"
                        first-number
                        last-number
                        next-class="next-item"
                        prev-class="prev-item"
                        size="sm"
                    >
                        <template #prev-text>
                            <font-awesome-icon icon="chevron-left" size="lg" />
                        </template>
                        <template #next-text>
                            <font-awesome-icon icon="chevron-right" size="lg" />
                        </template>
                    </b-pagination>
                </b-col>
            </b-row>
        </div>
    </div>
</template>

<script>
import useContactsLists from "@/composables/useContactsLists";
import { nextTick, onBeforeUnmount, onMounted, ref, watch } from "@vue/composition-api";
import {
    BButton,
    BCol,
    BContainer,
    BDropdown,
    BDropdownItem,
    BFormRadio,
    BPagination,
    BRow,
    BSpinner,
    BTable,
    VBTooltip,
} from "bootstrap-vue";
import { useToast } from "vue-toastification/composition";
import searchBar from "../../create/components/searchBar.vue";
import useEngageForm from "../../useEngageForm";

export default {
    components: {
        BButton,
        BCol,
        BContainer,
        BDropdown,
        BDropdownItem,
        BFormRadio,
        BPagination,
        BRow,
        BTable,
        BSpinner,
        searchBar,
    },
    directives: {
        "b-tooltip": VBTooltip,
    },
    setup(_, { root }) {
        const bvModal = root.$bvModal;
        const selected = ref(null);
        const { currentStep } = useEngageForm(bvModal);
        const toast = useToast();

        // Before we unmount the component, we'll remove all toastifications first, because there are "persistent" toasts when exceeding limits.
        onBeforeUnmount(toast.clear);

        const {
            fetchContactsLists,
            deleteOne,
            refetchData,
            resolvePlatformIcon,
            transformPlatformName,
            tableColumns,
            perPage,
            currentPage,
            total,
            dataMeta,
            perPageOptions,
            searchQuery,
            sortBy,
            isSortDirDesc,
            contactFilter,
            refTable,
            isLoading,
        } = useContactsLists(bvModal, false);
        const { goToStep, selectContactList, selectedEngageToEdit } = useEngageForm(bvModal);
        onMounted(async () => {
            if (selectedEngageToEdit.value) {
                currentStep.value.contactList = selectedEngageToEdit.value.contactList;
                currentStep.value.contactList.contacts = selectedEngageToEdit.value.contacts;
                currentStep.value.isDone = true;
            }

            if (currentStep.value?.contactList?.id) {
                selected.value = currentStep.value?.contactList?.id;
                selectContactList(currentStep.value?.contactList);
            }
        });

        // We need to watch for changes, because any change will undo the selection
        // and /or our selected item might be somewhere else now
        watch([() => refTable?.value?.localItems, isLoading, sortBy, isSortDirDesc], (newValues, oldValues) => {
            if (isLoading.value || !refTable?.value?.localItems.length) {
                return;
            }

            // Bootstrap table doesn't re-sort its internal array. We do this for them so the index we find lines up
            if (newValues[2] !== oldValues[2] || newValues[3] !== oldValues[3]) {
                const newSortBy = newValues[2];
                const sortModifier = newValues[3] ? -1 : 1;
                refTable.value.localItems.sort((contactListA, contactListB) => {
                    if (typeof contactListA[newSortBy] === "number" && typeof contactListB[newSortBy] === "number") {
                        return (contactListA[newSortBy] - contactListB[newSortBy]) * sortModifier;
                    }
                    return `${contactListA[newSortBy]}`.localeCompare(`${contactListB[newSortBy]}`) * sortModifier;
                });
            }

            nextTick(() => {
                const selectedIndex = refTable.value.localItems.findIndex((item) => item.id === selected.value);
                refTable.value.selectRow(selectedIndex);
            });
        });

        /**
         // Popover info
         */
        const popoverShow = ref(false);

        const onPopoverClose = () => {
            popoverShow.value = false;
        };

        // Filter on ID to make sure newest created is showed first
        sortBy.value = "id";
        isSortDirDesc.value = true;

        contactFilter.value = (contact) => contact.phone;

        tableColumns.pop();

        const columns = [{ key: "checkbox", label: "", tdClass: "template-radio-button-column" }, ...tableColumns];

        const rowClicked = (item, index) => {
            selected.value = item.id;
            refTable.value.selectRow(index);
            selectContactList(item);
        };

        const addList = () => goToStep(6);

        return {
            resolvePlatformIcon,
            transformPlatformName,
            columns,
            perPage,
            currentPage,
            total,
            dataMeta,
            perPageOptions,
            searchQuery,
            refTable,
            isLoading,
            popoverShow,
            selected,
            fetchContactsLists,
            deleteOne,
            refetchData,
            onPopoverClose,
            goToStep,
            rowClicked,
            addList,
        };
    },
};
</script>
